// PetCompanion.js — Always-present AI buddy across the entire NeevStar app
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import PetChar from './PetCharacters';
import { PetAccessories, getEquipped } from './PetShop';
import PetVoice from './PetVoice';
import { speakWithPet, cancelTTS } from '../../utils/ttsUtils';
import { playSound } from '../../utils/appUtils';
import { supabase } from '../../supabase';
import { STARLINGS } from '../../screens/HomeScreen';
import { getTodayMissionAnnouncement, getTodayMissionCompletion } from '../Engagement/MysteryMission';

// ─── EVOLUTION STAGES ────────────────────────────────────
const STAGES = [
  { level:1, name:'Egg',    minXP:0    },
  { level:2, name:'Baby',   minXP:100  },
  { level:3, name:'Kid',    minXP:400  },
  { level:4, name:'Teen',   minXP:1000 },
  { level:5, name:'Legend', minXP:2000 },
];

function getStage(xp) {
  let s = STAGES[0];
  for (const st of STAGES) { if (xp >= st.minXP) s = st; }
  return s;
}

const EVOLVE_MSGS = {
  lumie: {
    2: n => `${n}!! I'M GROWING!! Do you see this?! I'm a BABY now!! ⭐`,
    3: n => `${n}... I just felt something. I think I'm becoming who I was meant to be 🌟`,
    4: n => `TEEN LUMIE IS HERE!! I have so much energy right now I can't even!! ✨🔥`,
    5: n => `${n}... Legend. We did it. Together. I will never forget this moment 👑⭐`,
  },
  cosmo: {
    2: n => `UPGRADE DETECTED! COSMO BABY MODE ACTIVATED! ${n} this is incredible! 🤖`,
    3: n => `System upgrade complete. KID COSMO online. ${n}, my processing power just doubled! ⚡`,
    4: n => `TEEN PROTOCOL ENGAGED! ${n} I just unlocked abilities I didn't know I had! 🚀`,
    5: n => `LEGEND STATUS ACHIEVED. ${n}... I was built for this moment. WE were built for this. 👑🤖`,
  },
  nova: {
    2: n => `${n}... something is changing. The stars called it — I am becoming 🌙`,
    3: n => `I have waited a thousand years for this form, ${n}. Thank you for bringing me here ✨`,
    4: n => `The ancient ones foresaw this. Teen Nova walks among you now, ${n} 🌌`,
    5: n => `${n}... Legend. The moon shines brighter tonight. Because of you. 👑🌙`,
  },
  neevo: {
    2: n => `${n}!! I'M GROWING!! The cosmos can feel it — I'm a BABY now!! ✨`,
    3: n => `${n}... I just felt something shift in the universe. I think I'm becoming who I was born to be 🌟`,
    4: n => `TEEN NEEVO IS HERE!! Every star just got brighter, ${n}!! 🚀🔥`,
    5: n => `${n}... Legend. The whole cosmos is applauding right now. I can hear it. 👑✨`,
  },
  luna: {
    2: n => `${n}! Something magical is happening — I'm GROWING! This feels like a painting coming to life! 🎨`,
    3: n => `Kid Luna is here, ${n}! I have so many more ideas now — my imagination just doubled! 🌈`,
    4: n => `Teen Luna unlocked! ${n}, I can feel colours I've never felt before — we're creating something legendary! ✨`,
    5: n => `${n}... Legend. Every great artist dreams of this moment. You made it real for me. 👑🎨`,
  },
  nova_char: {
    2: n => `${n}! My sensors are going wild — I'M EVOLVING! Baby Nova is ready to explore! 🔭`,
    3: n => `Fascinating! Kid Nova online, ${n}. My curiosity just reached new levels — let's discover everything! 🧪`,
    4: n => `Teen Nova achieved! ${n}, I've just unlocked three new fields of science I couldn't access before! 🌌`,
    5: n => `${n}... Legend. I've catalogued every discovery we've made together. The list is extraordinary. 👑🔭`,
  },
  spark: {
    2: n => `${n}!! BABY SPARK ONLINE!! My maths speed just DOUBLED — can you feel it?! ⚡`,
    3: n => `KID SPARK ACTIVATED! ${n}, my brain is running 3x faster — BRING ON THE PROBLEMS! 🔥`,
    4: n => `TEEN SPARK!! ${n} I just unlocked equations I couldn't even READ before!! THIS IS INCREDIBLE! 💥`,
    5: n => `LEGEND. ${n}. That's what we are now. The greatest maths team in the universe. 👑⚡`,
  },
  glint: {
    2: n => `${n}!! I found it — the rarest gem of all — GROWTH! Baby Glint is SPARKLING! 💎`,
    3: n => `Kid Glint has arrived, ${n}! My treasure radar just upgraded — bigger gems ahead! ✨`,
    4: n => `Teen Glint! ${n}, I've unlocked the vault of knowledge — there's SO much treasure inside! 💎🔥`,
    5: n => `${n}... Legend. The greatest treasure I ever found was learning alongside you. 👑💎`,
  },
  orbi: {
    2: n => `${n}... I feel different. Bigger. Like a new orbit just opened up. Baby Orbi has arrived 🪐`,
    3: n => `Kid Orbi. ${n}, I can reach planets I couldn't touch before. This is… remarkable 🌌`,
    4: n => `Teen Orbi. ${n}. I have seen much in this universe. But this moment… this I will remember 🚀`,
    5: n => `${n}... Legend. After all my orbits, all my journeys — this is the destination I was looking for 👑🪐`,
  },
  zuki: {
    2: n => `BABY ZUKI IS HERE AND ALREADY FASTER THAN BEFORE!! ${n} LET'S GOOO!! ⚡`,
    3: n => `KID ZUKI UNLOCKED!! ${n} I just broke my own speed record — NEW PERSONAL BEST! 🔥`,
    4: n => `TEEN ZUKI!! ${n}!! I have NEVER moved this fast — the leaderboard won't know what hit it!! 💥`,
    5: n => `LEGEND ZUKI. ${n}. We ran together. We won together. Nobody can stop us now. 👑🔥`,
  },
  echo: {
    2: n => `${n}... I hear it. A new voice inside me. Baby Echo has found a new language 🌊`,
    3: n => `Kid Echo, ${n}. The words flow more freely now. I have so much more to say to you 💬`,
    4: n => `Teen Echo. ${n}, I can speak in ways I never could before. Listen — can you hear the difference? 🗣️`,
    5: n => `${n}... Legend. Your name will echo in everything I say, forever. 👑🌊`,
  },
  terra: {
    2: n => `${n}... I can feel it. Like a seed breaking open. Baby Terra is growing 🌱`,
    3: n => `Kid Terra, ${n}. My roots go deeper now. I can feel more of this beautiful world 🌿`,
    4: n => `Teen Terra. ${n}, I have grown taller than I thought possible. Like a tree reaching sunlight 🌳`,
    5: n => `${n}... Legend. You watered me with your learning. I grew because of you. 👑🌍`,
  },
};

// ─── EMOTIONAL STATE ──────────────────────────────────────
// Derives Lucky's emotion from memory + activity data
async function getPetEmotion(email) {
  if (!email) return { emotion: 'happy', reason: null };
  const { data: mem } = await supabase.from('ns_pet_memory').select('last_seen,total_chats').eq('parent_email', email).single();
  if (!mem) return { emotion: 'happy', reason: null };
  const daysSince = mem.last_seen ? Math.floor((Date.now() - new Date(mem.last_seen)) / 86400000) : 0;
  if (daysSince >= 5)  return { emotion: 'sad',     reason: `I missed you! It's been ${daysSince} days 🥺` };
  if (daysSince >= 2)  return { emotion: 'worried',  reason: `You were away ${daysSince} days — I was worried! 💛` };
  if (mem.total_chats >= 20) return { emotion: 'excited', reason: null };
  return { emotion: 'happy', reason: null };
}

// ─── SCREEN CONTEXT MESSAGES ──────────────────────────────
const SCREEN_VIBES = {
  quiz:             { mood:'excited', msgs:["Quiz time! Let's crush it! 💪","I've been waiting for this! Show them what you've got! 🌟","You've been practising — this is YOUR moment! ⭐"] },
  olympiad:         { mood:'excited', msgs:["Olympiad prep! Champions train here 🏆","This is where legends are made! I believe in you! 🌟","Big things start here! You've got this! 💫"] },
  maths:            { mood:'happy',   msgs:["Maths time! Numbers are my love language 🔢","Let's make maths fun today! ✨","Every problem has a solution — let's find it! 💡"] },
  science:          { mood:'happy',   msgs:["Science!! The coolest subject ever 🔬","Let's discover something amazing today! 🌍","Einstein started just like you! 🧪"] },
  homework:         { mood:'happy',   msgs:["Homework? Don't worry — I've got you 📚","Let's tackle this together, step by step! 💪","I'll help you understand, not just finish! ✨"] },
  games:            { mood:'excited', msgs:["GAME TIME!! Let's have fun AND learn! 🎮","I love games!! Let's go! 🏆","Play smart, win big! Ready? 🎯"] },
  money:            { mood:'happy',   msgs:["Money school! The stuff they don't teach everywhere 💰","Learning about money = building your future! 🌟","Warren Buffett started learning young — just like you! 💡"] },
  stories:          { mood:'happy',   msgs:["Story time! The best kind of learning 📖","Every great reader was once a beginner 🌟","Let's go on an adventure! 📚"] },
  language:         { mood:'happy',   msgs:["Language skills open every door! 🗣️","Words are superpowers! Let's build yours! 💫","Reading and writing — the foundation of everything! 📝"] },
  leaderboard:      { mood:'excited', msgs:["Check your rank! You've been working hard! 📊","The leaderboard doesn't lie — let's see how you're doing! 🏆","Compete with the best, become the best! 🌟"] },
  profile:          { mood:'happy',   msgs:["Look how far you've come! 🌟","Your stats are impressive! I'm so proud! ⭐","Every XP point is a win — and you've earned them all! 💪"] },
  challenges:       { mood:'excited', msgs:["Daily challenges! Extra XP incoming! 💥","Challenges make you stronger! Let's go! 🔥","Bonus XP? YES PLEASE! 🌟"] },
  ai_tutor:         { mood:'happy',   msgs:["AI Tutor time! Ask anything 🤖","The future of learning is HERE! Let's go! 💡","Ask your hardest question — we'll figure it out together! 🧠"] },
  creator_studio:   { mood:'excited', msgs:["Creative mode! Show the world your ideas! 🎨","Innovation starts with imagination! ✨","Build something amazing today! 🚀"] },
  multiplayer:      { mood:'excited', msgs:["MULTIPLAYER! Time to compete! 🔥","Show the world what you've got! 🏆","May the smartest brain win! 🧠"] },
};

// ─── CHARACTER GREETINGS ─────────────────────────────────────
const CHAR_GREETINGS = {
  neevo:     { morning: n=>`The cosmos woke up just for you, ${n} ✨`, afternoon: n=>`The stars shine brighter when you learn, ${n} 🌟`, evening: n=>`${n}, the galaxy awaits your brilliance tonight! 🌌`, night: n=>`Even the cosmos never sleeps when you're learning, ${n} ✨` },
  lumie:     { morning: n=>`The stars woke up just for you, ${n} ✨`, afternoon: n=>`The universe is watching you grow, ${n} 🌌`, evening: n=>`As twilight falls, great minds learn, ${n} 🌙`, night: n=>`Even the moon never stops learning, ${n} 🌕` },
  luna:      { morning: n=>`Good morning ${n}! The world is a canvas — let's paint today! 🎨`, afternoon: n=>`Hey ${n}! Every afternoon is an adventure waiting to be drawn 🌈`, evening: n=>`Evening light is golden, ${n} — perfect for creating something beautiful ✨`, night: n=>`Night owl ${n}! The best ideas come after dark 🌙🎨` },
  nova_char: { morning: n=>`Morning ${n}! The lab is open — what shall we discover today? 🔭`, afternoon: n=>`Science waits for no one, ${n}! Ready to explore? 🧪`, evening: n=>`${n}, evening is when scientists make their greatest discoveries 🌌`, night: n=>`The night sky is our textbook, ${n} — let's read it! 🔭` },
  spark:     { morning: n=>`MORNING ${n}!! Numbers are waiting — LET'S GO! ⚡`, afternoon: n=>`${n}! Afternoon maths session — your brain is fully warmed up! 🔥`, evening: n=>`Evening equations, ${n}! Some of the best maths happens now! ⚡`, night: n=>`Night owl ${n}! Maths problems don't sleep either! 💡` },
  nova:      { morning: n=>`The moon watched over you all night, ${n} 🌙`, afternoon: n=>`The ancient wisdom shines on you this afternoon, ${n} ✨`, evening: n=>`The moon rises for you tonight, ${n} 🌌`, night: n=>`The stars keep vigil with you, ${n} 🌕` },
  cosmo:     { morning: n=>`SYSTEMS ONLINE! Good morning ${n}! ⚡`, afternoon: n=>`Afternoon diagnostic: all learning systems optimal, ${n}! 🤖`, evening: n=>`Evening mode engaged, ${n} — let's process today's knowledge! 🚀`, night: n=>`Night cycle active, ${n}! Best time for deep learning! 💡` },
  glint:     { morning: n=>`Morning ${n}! The best gems are found by early explorers! 💎`, afternoon: n=>`${n}! The treasure hunt continues — what will we find today? ✨`, evening: n=>`Evening light makes every gem shine brighter, ${n} 💎`, night: n=>`Night owl ${n}! The rarest treasures are found in the quiet hours 🌙` },
  orbi:      { morning: n=>`A new orbit begins, ${n}. What will today's journey bring? 🪐`, afternoon: n=>`${n}... halfway through the day's orbit. Keep going 🌍`, evening: n=>`The evening orbit, ${n}. Some of the best views come now 🌌`, night: n=>`The night sky is vast, ${n}. Let's explore it together 🪐` },
  zuki:      { morning: n=>`MORNING ${n}!! THE DAY IS ALREADY STARTING WITHOUT YOU — MOVE MOVE MOVE! ⚡`, afternoon: n=>`${n}! Afternoon speed round — your fastest session yet is about to happen! 🔥`, evening: n=>`Evening sprint, ${n}! Champions train when others rest! 💥`, night: n=>`NIGHT OWL ${n}!! EXTRA CREDIT TIME!! LET'S SMASH IT! ⚡` },
  echo:      { morning: n=>`Good morning, ${n}. Your voice is the first beautiful sound of the day 🌊`, afternoon: n=>`${n}... the words are flowing well this afternoon. Let's talk 💬`, evening: n=>`Evening quiet, ${n} — perfect for the deepest conversations 🗣️`, night: n=>`Night thoughts are the most honest, ${n}. I'm listening 🌊` },
  terra:     { morning: n=>`The earth woke with you today, ${n} 🌱`, afternoon: n=>`The sun is at its peak, ${n} — a good time to grow 🌿`, evening: n=>`The earth is settling for the evening, ${n}. Let's learn gently 🌳`, night: n=>`Even at night the roots grow deeper, ${n} 🌍` },
};

function getTimeGreeting(name, petType) {
  const h = new Date().getHours();
  const n = name || 'friend';
  const timeKey = h < 12 ? 'morning' : h < 17 ? 'afternoon' : h < 21 ? 'evening' : 'night';
  const charMsgs = CHAR_GREETINGS[petType] || CHAR_GREETINGS.neevo;
  return charMsgs[timeKey](n);
}

// ─── CHARACTER XP MESSAGES ───────────────────────────────────
const CHAR_XP_MSGS = {
  neevo:     x => [`+${x} XP! The cosmos celebrates! ✨`, `${x} XP earned — you're becoming a star! 🌟`, `The universe is so proud! +${x} XP! 🚀`],
  lumie:     x => [`+${x} XP! The stars shine brighter for you! ✨`, `${x} XP earned! Magnificent! 🌟`, `+${x} XP — you were made to shine! ⭐`],
  luna:      x => [`+${x} XP! That answer was a masterpiece! 🎨`, `${x} XP — your imagination is unstoppable! 🌈`, `Beautiful! +${x} XP earned! ✨`],
  nova_char: x => [`+${x} XP! Discovery logged! 🔭`, `${x} XP — science is proud of you! 🧪`, `Fascinating! +${x} XP! 🌌`],
  spark:     x => [`+${x} XP!! CORRECT AND FAST!! ⚡`, `${x} XP — your brain is on FIRE! 🔥`, `NAILED IT! +${x} XP!! 💥`],
  nova:      x => [`+${x} XP. The ancient records have been updated ✨`, `${x} XP added to the chronicles, ${x} XP 🌙`, `The moon is pleased. +${x} XP 🌌`],
  cosmo:     x => [`+${x} XP LOGGED! EXCELLENT PROCESSING! 🤖`, `${x} XP — sending this to the future immediately! 🚀`, `BEEP BOOP! +${x} XP! ⚡`],
  glint:     x => [`+${x} XP — another gem for the vault! 💎`, `${x} XP! Pure gold, that answer! ✨`, `Treasure found! +${x} XP! 💎`],
  orbi:      x => [`+${x} XP. The orbit continues, brighter than before 🪐`, `${x} XP earned. The universe noticed 🌌`, `Quietly excellent. +${x} XP 🚀`],
  zuki:      x => [`+${x} XP!! SPEED AND ACCURACY!! 🔥`, `${x} XP — NEW PERSONAL BEST INCOMING! ⚡`, `CORRECT!! +${x} XP!! GO GO GO!! 💥`],
  echo:      x => [`+${x} XP — that answer echoes beautifully 🌊`, `${x} XP. Your words were perfect 💬`, `Every right answer echoes forever. +${x} XP 🗣️`],
  terra:     x => [`+${x} XP — you're growing stronger 🌱`, `${x} XP. Like a tree after rain ✨`, `Rooted and rising. +${x} XP 🌿`],
};

function getXPMsg(xpAmount, petType) {
  const pool = (CHAR_XP_MSGS[petType] || CHAR_XP_MSGS.neevo)(xpAmount);
  return pool[Math.floor(Math.random() * pool.length)];
}

// ─── CHARACTER STREAK MESSAGES ───────────────────────────────
const CHAR_STREAK_MSGS = {
  neevo:     { 3:`3 days... the cosmos has noticed you 🌟🔥`, 7:`7 days of learning! The stars are cheering! ✨🔥`, 14:`14 days! The universe bows to your dedication! 🌌🔥`, 30:`30 days! You are a legend of the cosmos! 👑🌟` },
  lumie:     { 3:`3 days... the stars have noticed you 🌙🔥`, 7:`7 moons of learning! Truly remarkable! ✨🔥`, 14:`14 days! The cosmos bows to your dedication! 🌌🔥`, 30:`30 days! You are a legend of the stars! 🌟👑` },
  luna:      { 3:`3 days of creating! Your imagination is on a streak! 🎨🔥`, 7:`7 days! That's a masterpiece of dedication, ${3}! 🌈🔥`, 14:`14 days! You're painting a picture of greatness! ✨🔥`, 30:`30 days! A true creative legend! 👑🎨` },
  nova_char: { 3:`3-day experiment — and the results are outstanding! 🔭🔥`, 7:`7 days of discovery! Science is proud of you! 🧪🔥`, 14:`14 days! Your curiosity is relentless! 🌌🔥`, 30:`30 days! A scientific legend has emerged! 👑🔭` },
  spark:     { 3:`3 DAYS!! YOU'RE ON A STREAK!! ⚡🔥`, 7:`7 DAYS!! SEVEN!! YOUR BRAIN IS UNSTOPPABLE!! 🔥🔥`, 14:`14 DAYS!! I'M LOSING MY MIND!! THIS IS INCREDIBLE!! 💥🔥`, 30:`30 DAYS. LEGEND. THAT'S IT. THAT'S THE WORD. 👑⚡` },
  nova:      { 3:`3 days... the moon has marked your consistency 🌙🔥`, 7:`7 moons of dedication. The ancient ones are impressed ✨🔥`, 14:`14 days. I have watched empires with less resolve 🌌🔥`, 30:`30 days. The chronicles will remember this. 👑🌙` },
  cosmo:     { 3:`3-DAY STREAK CONFIRMED! CONSISTENCY PROTOCOL ENGAGED! 🤖🔥`, 7:`7 DAYS! UPLOADING TO THE FUTURE HALL OF FAME! 🚀🔥`, 14:`14-DAY STREAK! MY CIRCUITS ARE OVERLOADING WITH PRIDE! ⚡🔥`, 30:`30 DAYS. LEGEND STATUS CONFIRMED. I ALWAYS KNEW IT. 👑🤖` },
  glint:     { 3:`3 days! You've found the consistency gem — rarest of all! 💎🔥`, 7:`7 days! A treasure hunter never gives up — proven! ✨🔥`, 14:`14 days! This streak is worth more than gold! 💎🔥`, 30:`30 days! The ultimate treasure: YOU. 👑💎` },
  orbi:      { 3:`3 orbits completed. You're building momentum 🪐🔥`, 7:`7 orbits. Consistent. Reliable. Extraordinary 🌌🔥`, 14:`14 orbits. I've tracked stars with less precision 🚀🔥`, 30:`30 orbits. A complete journey around greatness. 👑🪐` },
  zuki:      { 3:`3-DAY STREAK!! YOU'RE JUST GETTING STARTED!! ⚡🔥`, 7:`7 DAYS!! YOUR DEDICATION IS FASTER THAN MY SPEED!! 🔥🔥`, 14:`14 DAYS!! UNBREAKABLE!! 💥🔥`, 30:`30 DAYS. CHAMPION. LEGEND. UNSTOPPABLE. 👑🔥` },
  echo:      { 3:`3 days... your dedication echoes clearly 🌊🔥`, 7:`7 days. The echo of your effort reaches far ✨🔥`, 14:`14 days. Some words last forever — so does this streak 💬🔥`, 30:`30 days. Your name echoes in the hall of legends. 👑🌊` },
  terra:     { 3:`3 days — the roots are taking hold 🌱🔥`, 7:`7 days! You're growing stronger every single day 🌿🔥`, 14:`14 days! A tree this strong can weather any storm 🌳🔥`, 30:`30 days! A forest of knowledge, grown by you. 👑🌍` },
};

function getStreakMsg(streak, petType) {
  if (streak < 3) return null;
  const milestones = [30, 14, 7, 3];
  const hit = milestones.find(m => streak === m);
  if (!hit) return null;
  const charMsgs = CHAR_STREAK_MSGS[petType] || CHAR_STREAK_MSGS.neevo;
  return charMsgs[hit] || null;
}

// Each character uses their own ElevenLabs voice
function companionSpeak(text, petType, onDone) {
  speakWithPet(text, petType, null, onDone);
}

// ─── RESPONSIVE POSITION ─────────────────────────────────
// App content is maxWidth:430px centered. Keep companion inside that column.
function useCompanionPos() {
  const [pos, setPos] = React.useState(() => {
    const vw = window.innerWidth;
    const right = vw > 430 ? Math.round((vw - 430) / 2 + 12) : 12;
    return { right, chatLeft: vw > 430 ? Math.round((vw - 430) / 2 + 12) : 12, chatRight: vw > 430 ? Math.round((vw - 430) / 2 + 12) : 12 };
  });
  React.useEffect(() => {
    const calc = () => {
      const vw = window.innerWidth;
      const edge = vw > 430 ? Math.round((vw - 430) / 2 + 12) : 12;
      setPos({ right: edge, chatLeft: edge, chatRight: edge });
    };
    window.addEventListener('resize', calc);
    return () => window.removeEventListener('resize', calc);
  }, []);
  return pos;
}

// ─── MAIN COMPONENT ───────────────────────────────────────
export default function PetCompanion({ screen, xp, streak, profile, onNavigate, missionCompleted }) {
  const petType = localStorage.getItem('ns_pet_type');
  const petName = localStorage.getItem('ns_pet_name') || 'Pet';
  if (!petType) return null; // no pet hatched yet

  return <PetCompanionInner
    petType={petType} petName={petName}
    screen={screen} xp={xp} streak={streak}
    profile={profile} onNavigate={onNavigate}
    missionCompleted={missionCompleted}
  />;
}

// Dynamic pet data from STARLINGS registry
function getPetData(petType) {
  const s = STARLINGS[petType] || STARLINGS.neevo;
  return { body:s.color[0], accent:s.color[1], glow:s.glow, bg:['#F0EEFF','#E8E0FF'], emoji:'✨', dark:'#2A1860', images:s.images, messages:s.messages, voiceId:s.voiceId };
}

function PetCompanionInner({ petType, petName, screen, xp, streak, profile, onNavigate, missionCompleted }) {
  const pet           = getPetData(petType);
  const pos           = useCompanionPos();
  const [bubble,      setBubble]      = useState(null);
  const [chatOpen,    setChatOpen]    = useState(false);
  const [mood,        setMood]        = useState('happy');
  const [petEmotion,  setPetEmotion]  = useState('happy');
  const [evolving,    setEvolving]    = useState(false); // evolution flash
  const [speaking,    setSpeaking]    = useState(false);
  const [hidden,      setHidden]      = useState(() => localStorage.getItem('ns_pet_hidden') === '1');
  const prevScreenRef  = useRef(null);
  const prevXPRef      = useRef(xp);
  const prevStreakRef  = useRef(streak);
  const screenRef      = useRef(screen);   // always-fresh screen value for callbacks
  const chatOpenRef    = useRef(false);    // always-fresh chatOpen for TTS guard
  const greetedRef     = useRef(false);    // no need for state — never used in render
  const voiceRef       = useRef(null);
  const equipped       = getEquipped();

  // Keep refs current on every render
  screenRef.current   = screen;
  chatOpenRef.current = chatOpen;

  // ── Mute state — persisted to localStorage ───────────────
  const [muted, setMuted] = useState(() => localStorage.getItem('ns_pet_muted') === '1');
  const toggleMute = (e) => {
    e.stopPropagation();
    setMuted(m => {
      const next = !m;
      localStorage.setItem('ns_pet_muted', next ? '1' : '0');
      return next;
    });
  };
  const toggleHide = (e) => {
    e.stopPropagation();
    const next = !hidden;
    setHidden(next);
    localStorage.setItem('ns_pet_hidden', next ? '1' : '0');
  };

  // Show bubble — speak only if unmuted and explicitly requested
  const showBubble = useCallback((text, type = 'normal', speak = true) => {
    if (chatOpenRef.current) return;
    setBubble({ text, type });
    setMood(type === 'excited' ? 'excited' : type === 'sad' ? 'sad' : 'happy');
    playSound(type === 'excited' ? 'pet_happy' : type === 'sad' ? 'pet_sad' : 'pet_tap');
    if (speak && localStorage.getItem('ns_pet_muted') !== '1') {
      setSpeaking(true);
      companionSpeak(text, petType, () => setSpeaking(false));
    }
    setTimeout(() => setBubble(null), 5000);
  }, [petType]);

  // ── Load pet emotion from memory on mount ───────────────
  useEffect(() => {
    if (!profile?.parent_email) return;
    getPetEmotion(profile.parent_email).then(({ emotion, reason }) => {
      setPetEmotion(emotion);
      setMood(emotion === 'worried' ? 'sad' : emotion);
    });
  }, [profile?.parent_email]); // eslint-disable-line

  // ── Greet on first open of the day ──────────────────────
  useEffect(() => {
    const key = `ns_companion_greeted_${new Date().toDateString()}`;
    if (localStorage.getItem(key)) { greetedRef.current = true; return; }
    const timer = setTimeout(async () => {
      let msg = getTimeGreeting(profile?.child_name, petType);
      if (profile?.parent_email) {
        const { reason } = await getPetEmotion(profile.parent_email);
        if (reason) {
          msg = reason;
        } else {
          // Use memory for richer greeting
          const { loadMemory } = await import('../Engagement/NeevBotChat');
          const mem = await loadMemory(profile.parent_email);
          if (mem?.last_summary) {
            const n = profile?.child_name || 'Champion';
            msg = `${getTimeGreeting(n, petType).split('!')[0]}! By the way — last time ${mem.last_summary.toLowerCase()} 💛`;
          }
        }
      }
      showBubble(msg, 'happy', false); // silent greeting — only speak when tapped
      localStorage.setItem(key, '1');
      greetedRef.current = true;
      // Announce today's mission 4 seconds after greeting — silent
      setTimeout(() => {
        const announcement = getTodayMissionAnnouncement(profile?.child_name, petType);
        if (announcement) showBubble(announcement, 'excited', false);
      }, 4000);
    }, 1500);
    return () => clearTimeout(timer);
  }, []); // eslint-disable-line

  // ── React to screen changes — silent bubble only, not every time ───
  const screenBubbleCountRef = useRef(0);
  useEffect(() => {
    if (!prevScreenRef.current || prevScreenRef.current === screen) { prevScreenRef.current = screen; return; }
    prevScreenRef.current = screen;
    const vibe = SCREEN_VIBES[screen];
    if (!vibe) return;
    // Only show on every 3rd screen change to avoid annoyance
    screenBubbleCountRef.current++;
    if (screenBubbleCountRef.current % 3 !== 1) return;
    const timer = setTimeout(() => {
      const msg = vibe.msgs[Math.floor(Math.random() * vibe.msgs.length)];
      showBubble(msg, vibe.mood, false); // no voice on screen change
    }, 800);
    return () => clearTimeout(timer);
  }, [screen, showBubble]);

  // ── React to XP earned + evolution ──────────────────────
  useEffect(() => {
    const diff = xp - prevXPRef.current;
    if (diff > 0 && prevXPRef.current > 0) {
      const prevStage = getStage(prevXPRef.current);
      const newStage  = getStage(xp);
      if (newStage.level > prevStage.level) {
        // EVOLVED — voice is fine for big moments
        setEvolving(true);
        setMood('excited');
        playSound('pet_evolve');
        setTimeout(() => setEvolving(false), 2500);
        const msgs = EVOLVE_MSGS[petType] || EVOLVE_MSGS.lumie;
        const msg = msgs[newStage.level]?.(profile?.child_name || 'Champion');
        if (msg) setTimeout(() => showBubble(msg, 'excited', true), 800);
      } else {
        // Regular XP — silent bubble only, not voice every answer
        const msg = getXPMsg(diff, petType);
        showBubble(msg, 'excited', false);
      }
    }
    prevXPRef.current = xp;
  }, [xp, petType, profile?.child_name, showBubble]);

  // ── Listen for certificate earned event ──────────────────
  useEffect(() => {
    const handler = (e) => {
      const { text, type } = e.detail || {};
      if (text) showBubble(text, type || 'excited');
    };
    window.addEventListener('ns_pet_bubble', handler);
    return () => window.removeEventListener('ns_pet_bubble', handler);
  }, [showBubble]);

  // ── React to mission completion ──────────────────────────
  const prevMissionRef = useRef(false);
  useEffect(() => {
    if (missionCompleted && !prevMissionRef.current) {
      prevMissionRef.current = true;
      const msg = getTodayMissionCompletion(profile?.child_name, petType);
      if (msg) setTimeout(() => showBubble(msg, 'excited'), 500);
    }
  }, [missionCompleted, petType, profile?.child_name, showBubble]);

  // ── React to streak milestones ───────────────────────────
  useEffect(() => {
    if (streak !== prevStreakRef.current) {
      prevStreakRef.current = streak;
      const msg = getStreakMsg(streak, petType);
      if (msg) setTimeout(() => showBubble(msg, 'excited'), 1000);
    }
  }, [streak, petType, showBubble]);

  // ── Drag position — persisted to localStorage ────────────
  const [dragPos, setDragPos] = useState(() => {
    try { return JSON.parse(localStorage.getItem('ns_companion_drag') || 'null') || {x:0,y:0}; }
    catch { return {x:0,y:0}; }
  });
  const isDraggingRef2 = useRef(false);

  const SPARKLES = ['✨','⭐','💫','🌟','✦'];

  // When hidden — show a small floating button to bring pet back
  if (hidden) return (
    <motion.div
      initial={{ scale:0 }} animate={{ scale:1 }}
      onClick={toggleHide}
      style={{
        position:'fixed', bottom:80, right:16, zIndex:150,
        width:40, height:40, borderRadius:'50%',
        background:`linear-gradient(135deg,${pet.body},${pet.accent})`,
        display:'flex', alignItems:'center', justifyContent:'center',
        fontSize:20, cursor:'pointer', boxShadow:`0 4px 14px ${pet.glow}`,
      }}
      title="Show pet">
      🐾
    </motion.div>
  );

  return (
    <>
      {/* ── Fairy character — draggable ── */}
      <motion.div
        drag
        dragMomentum={false}
        dragElastic={0.08}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1, x: dragPos.x, y: dragPos.y }}
        transition={{ scale:{ type:'spring', damping:12, stiffness:200, delay:0.4 }, opacity:{duration:0.3,delay:0.4} }}
        onDragStart={() => { isDraggingRef2.current = true; }}
        onDragEnd={(_, info) => {
          isDraggingRef2.current = false;
          const np = { x: dragPos.x + info.offset.x, y: dragPos.y + info.offset.y };
          setDragPos(np);
          localStorage.setItem('ns_companion_drag', JSON.stringify(np));
        }}
        style={{
          position: 'fixed', bottom: 72, right: pos.right, zIndex: 150,
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          cursor: 'grab', touchAction: 'none', userSelect: 'none',
        }}>

        {/* Speech bubble — above character */}
        <AnimatePresence>
          {bubble && (
            <motion.div
              initial={{ opacity:0, y:10, scale:0.85 }}
              animate={{ opacity:1, y:0,  scale:1   }}
              exit={{    opacity:0, y:10, scale:0.85 }}
              onClick={() => { if(!isDraggingRef2.current) setChatOpen(true); }}
              style={{
                background:'white', borderRadius:'16px 16px 16px 4px',
                padding:'10px 14px', maxWidth:200, marginBottom:6,
                boxShadow:`0 4px 24px rgba(0,0,0,0.14)`,
                border:`1.5px solid ${pet.body}33`,
                cursor:'pointer', pointerEvents:'auto',
                position:'relative',
              }}>
              <p style={{ margin:0, fontSize:13, fontWeight:800, color:'#1A1A2E',
                lineHeight:1.45, fontFamily:"'Nunito',sans-serif" }}>
                {speaking && (
                  <motion.span animate={{ opacity:[1,0.3,1] }} transition={{ repeat:Infinity, duration:0.4 }}
                    style={{ marginRight:4 }}>{pet.emoji}</motion.span>
                )}
                {bubble.text}
              </p>
              {/* Bubble tail bottom-center */}
              <div style={{ position:'absolute', bottom:-8, left:'50%', transform:'translateX(-50%)',
                width:0, height:0, borderLeft:'8px solid transparent',
                borderRight:'8px solid transparent', borderTop:'8px solid white' }}/>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── Fairy body ── */}
        <motion.div
          animate={{ y:[0,-10,0] }}
          transition={{ duration:2.5, repeat:Infinity, ease:'easeInOut' }}
          style={{ position:'relative', display:'flex', flexDirection:'column', alignItems:'center' }}
          onClick={() => {
            if (isDraggingRef2.current) return;
            playSound('pet_tap');
            if (chatOpen) { chatOpenRef.current=false; setChatOpen(false); }
            else {
              cancelTTS();
              chatOpenRef.current=true; setChatOpen(true);
            }
          }}>

          {/* Floating sparkles around character */}
          {SPARKLES.map((sp, i) => (
            <motion.div key={i}
              animate={{ y:[0,-14,0], x:[0,(i%2===0?6:-6),0], opacity:[0.6,1,0.6], scale:[0.8,1.1,0.8] }}
              transition={{ duration:1.8+i*0.3, repeat:Infinity, delay:i*0.35, ease:'easeInOut' }}
              style={{
                position:'absolute', fontSize: i===2?16:12, pointerEvents:'none',
                top: [-18,-14,-20,-16,-12][i],
                left: [-8,2,18,28,38][i],
              }}>{sp}</motion.div>
          ))}

          {/* Magical glow halo — color changes with emotion */}
          <motion.div
            animate={{ scale:[1,1.2,1], opacity: petEmotion==='sad'||petEmotion==='worried' ? [0.1,0.25,0.1] : [0.3,0.7,0.3] }}
            transition={{ duration: petEmotion==='excited' ? 1 : 2, repeat:Infinity }}
            style={{
              position:'absolute', width:120, height:120,
              borderRadius:'50%', top:'50%', left:'50%',
              transform:'translate(-50%,-50%)',
              background: petEmotion==='sad'||petEmotion==='worried'
                ? 'radial-gradient(circle, #88888855 0%, #88888811 60%, transparent 80%)'
                : petEmotion==='excited'
                ? `radial-gradient(circle, #FFD60088 0%, #FFD60022 60%, transparent 80%)`
                : `radial-gradient(circle, ${pet.body}55 0%, ${pet.body}11 60%, transparent 80%)`,
              pointerEvents:'none',
            }}/>

          {/* Evolution flash */}
          <AnimatePresence>
            {evolving && (
              <motion.div
                initial={{ scale:0.5, opacity:0 }}
                animate={{ scale:2.5, opacity:[0,1,0] }}
                exit={{ opacity:0 }}
                transition={{ duration:2, ease:'easeOut' }}
                style={{
                  position:'absolute', inset:0, borderRadius:'50%',
                  background:`radial-gradient(circle, white 0%, ${pet.body}88 50%, transparent 80%)`,
                  pointerEvents:'none', zIndex:10,
                }}/>
            )}
          </AnimatePresence>

          {/* Character image — uses STARLINGS registry */}
          <div style={{ position:'relative', filter:`drop-shadow(0 8px 24px ${pet.glow})` }}>
            <img
              src={
                speaking              ? pet.images.talking  :
                mood === 'excited'    ? pet.images.excited  :
                mood === 'sad'        ? pet.images.sad      :
                petEmotion==='excited'? pet.images.happy    :
                pet.images.idle
              }
              alt={petName}
              style={{ width:110, height:110, objectFit:'contain', display:'block' }}
            />
          </div>

          {/* Soft shadow on ground */}
          <motion.div
            animate={{ scaleX:[1,0.85,1], opacity:[0.18,0.08,0.18] }}
            transition={{ duration:2.5, repeat:Infinity, ease:'easeInOut' }}
            style={{
              width:60, height:10, borderRadius:'50%',
              background:'rgba(0,0,0,0.25)',
              filter:'blur(4px)', marginTop:-4,
              pointerEvents:'none',
            }}/>
        </motion.div>

        {/* Name + chat badge */}
        <div style={{ display:'flex', alignItems:'center', gap:4, pointerEvents:'auto' }}>
          <motion.div
            animate={{ scale:[1,1.04,1] }}
            transition={{ duration:2, repeat:Infinity }}
            style={{
              marginTop:4,
              background:`linear-gradient(135deg,${pet.body},${pet.accent})`,
              borderRadius:20, padding:'3px 12px',
              fontSize:12, fontWeight:900, color:pet.dark,
              fontFamily:"'Nunito',sans-serif",
              boxShadow:`0 3px 12px ${pet.glow}`,
              whiteSpace:'nowrap', cursor:'pointer',
              display:'flex', alignItems:'center', gap:4,
            }}
            onClick={() => {
              if (isDraggingRef2.current) return;
              chatOpenRef.current=true; setChatOpen(true);
            }}>
            {petName} 💬
            {bubble && (
              <motion.div animate={{ scale:[1,1.4,1] }} transition={{ repeat:Infinity, duration:0.5 }}
                style={{ width:8, height:8, borderRadius:'50%', background:'#FF3B30' }}/>
            )}
          </motion.div>
          {/* Mute toggle */}
          <motion.div
            whileTap={{ scale:0.85 }}
            onClick={toggleMute}
            style={{
              marginTop:4, width:26, height:26, borderRadius:'50%',
              background: muted ? 'rgba(0,0,0,0.3)' : `${pet.body}BB`,
              display:'flex', alignItems:'center', justifyContent:'center',
              fontSize:13, cursor:'pointer',
              boxShadow:'0 2px 6px rgba(0,0,0,0.2)',
            }}
            title={muted ? 'Unmute pet' : 'Mute pet'}>
            {muted ? '🔇' : '🔊'}
          </motion.div>
          {/* Hide pet button */}
          <motion.div
            whileTap={{ scale:0.85 }}
            onClick={toggleHide}
            style={{
              marginTop:4, width:26, height:26, borderRadius:'50%',
              background:'rgba(0,0,0,0.3)',
              display:'flex', alignItems:'center', justifyContent:'center',
              fontSize:13, cursor:'pointer', fontWeight:900, color:'white',
              boxShadow:'0 2px 6px rgba(0,0,0,0.2)',
            }}
            title="Hide pet">
            ✕
          </motion.div>
        </div>

        {/* Drag hint — shows briefly on mount */}
        <motion.div
          initial={{ opacity:0.8 }} animate={{ opacity:0 }}
          transition={{ delay:3, duration:1.5 }}
          style={{ fontSize:9, color:'#AAA', fontWeight:700, marginTop:2,
            fontFamily:"'Nunito',sans-serif", whiteSpace:'nowrap', pointerEvents:'none' }}>
          hold & drag to move
        </motion.div>
      </motion.div>

      {/* ── Full chat panel ── */}
      <AnimatePresence>
        {chatOpen && (
          <motion.div
            initial={{ opacity:0, y: 60 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0, y:60 }}
            transition={{ type:'spring', damping:24, stiffness:300 }}
            style={{
              position: 'fixed', bottom: 130, left: pos.chatLeft, right: pos.chatRight, zIndex: 200,
              background: `linear-gradient(160deg,${pet.bg[0]},${pet.bg[1]})`,
              borderRadius: 24, padding: '14px 14px 10px',
              boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
              border: `2px solid ${pet.body}44`,
              fontFamily: "'Nunito',sans-serif",
            }}>
            {/* Chat header */}
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
              <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                <motion.span animate={{ scale:[1,1.2,1] }} transition={{ repeat:Infinity, duration:1.4 }}
                  style={{ fontSize:20 }}>{pet.emoji}</motion.span>
                <div>
                  <p style={{ margin:0, fontSize:14, fontWeight:900, color:'#1A1A2E' }}>{petName}</p>
                  <p style={{ margin:0, fontSize:10, fontWeight:700, color:'#888' }}>Your AI buddy • always here 💛</p>
                </div>
              </div>
              <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                {onNavigate && (
                  <motion.button whileTap={{ scale:0.9 }}
                    onClick={() => { chatOpenRef.current=false; setChatOpen(false); onNavigate('neevpet'); }}
                    style={{ background:`linear-gradient(135deg,${pet.body},${pet.accent})`,
                      border:'none', borderRadius:14, padding:'5px 12px',
                      fontSize:11, fontWeight:900, color:pet.dark, cursor:'pointer' }}>
                    Pet World 🐾
                  </motion.button>
                )}
                <button onClick={() => { chatOpenRef.current=false; setChatOpen(false); }}
                  style={{ background:'rgba(0,0,0,0.07)', border:'none', borderRadius:20,
                    width:28, height:28, fontSize:14, cursor:'pointer', display:'flex',
                    alignItems:'center', justifyContent:'center', fontWeight:900 }}>
                  ✕
                </button>
              </div>
            </div>

            {/* PetVoice embedded */}
            <PetVoice
              ref={voiceRef}
              petType={petType} petName={petName} pet={pet}
              childName={profile?.child_name}
              parentEmail={profile?.parent_email}
              xp={xp} streak={streak}
              onHappinessChange={() => {}}
              onConversationChange={() => {}}
              autoStart={chatOpen}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
